import { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, MailX, XCircle } from 'lucide-react';
import { BRAND_WORDMARK } from '../lib/brand';
import { BRANDING, brandingLocalita } from '../lib/branding';

/**
 * Le pagine che vede chi riceve le nostre email: disiscrizione, preferenze,
 * conferma dell'iscrizione. Si aprono senza login.
 *
 * Stanno qui e non nella funzione email-public perche' Supabase, sul suo
 * dominio, mostra le pagine web come testo semplice. La funzione fa il lavoro
 * e rimanda qui chi apre il link; questa pagina la richiama.
 *
 * Aprire il link non disiscrive: serve il bottone. I programmi che controllano
 * i link delle mail (antivirus, Outlook) li aprono da soli, e disiscriverebbero
 * persone che non l'hanno chiesto.
 */

const FUNZIONE = `${String(import.meta.env.VITE_SUPABASE_URL ?? '').replace(/\/+$/, '')}/functions/v1/email-public`;

type Azione = 'disiscrizione' | 'preferenze' | 'conferma';

type Stato =
  | { fase: 'caricamento' }
  | { fase: 'non_valido' }
  | { fase: 'errore' }
  | { fase: 'da_confermare'; email: string }
  | { fase: 'gia_fuori'; email: string }
  | { fase: 'disiscritto'; email: string }
  | { fase: 'iscrizione'; esito: 'confermata' | 'gia_attiva' | 'scaduta' | 'non_valida' };

async function chiama(percorso: string, token: string, metodo: 'GET' | 'POST') {
  const risposta = await fetch(`${FUNZIONE}/${percorso}?t=${encodeURIComponent(token)}`, {
    method: metodo,
    headers: { Accept: 'application/json' },
  });
  const corpo = await risposta.json().catch(() => ({}));
  return { ok: risposta.ok, corpo, stato: risposta.status };
}

const TESTI_ISCRIZIONE = {
  confermata: {
    titolo: 'Iscrizione confermata',
    testo: 'Da adesso riceverai le nostre comunicazioni. Puoi disiscriverti quando vuoi: il link è in fondo a ogni messaggio.',
  },
  gia_attiva: { titolo: 'Già confermata', testo: 'La tua iscrizione era già attiva: non devi fare altro.' },
  scaduta: {
    titolo: 'Link scaduto',
    testo: 'Questo link di conferma è scaduto. Iscriviti di nuovo dal sito e te ne manderemo uno nuovo.',
  },
  non_valida: { titolo: 'Link non valido', testo: 'Questo link di conferma non risulta più valido.' },
} as const;

export default function NewsletterPage() {
  const { azione } = useParams<{ azione: Azione }>();
  const [parametri] = useSearchParams();
  const token = parametri.get('t') ?? '';
  const [stato, setStato] = useState<Stato>({ fase: 'caricamento' });
  const [lavorando, setLavorando] = useState(false);

  useEffect(() => {
    if (!token) {
      setStato({ fase: 'non_valido' });
      return;
    }
    (async () => {
      try {
        if (azione === 'conferma') {
          const { corpo } = await chiama('conferma', token, 'POST');
          setStato({ fase: 'iscrizione', esito: corpo.esito ?? 'non_valida' });
          return;
        }
        const { ok, corpo } = await chiama('stato', token, 'GET');
        if (!ok) setStato({ fase: 'non_valido' });
        else if (corpo.attivo) setStato({ fase: 'da_confermare', email: corpo.email });
        else setStato({ fase: 'gia_fuori', email: corpo.email });
      } catch {
        setStato({ fase: 'errore' });
      }
    })();
  }, [azione, token]);

  const disiscrivi = useCallback(async () => {
    if (stato.fase !== 'da_confermare') return;
    setLavorando(true);
    try {
      const { ok } = await chiama('disiscrizione', token, 'POST');
      setStato(ok ? { fase: 'disiscritto', email: stato.email } : { fase: 'errore' });
    } catch {
      setStato({ fase: 'errore' });
    } finally {
      setLavorando(false);
    }
  }, [stato, token]);

  const titoloPagina = azione === 'preferenze' ? 'Le tue preferenze' : 'Annulla iscrizione';

  return (
    <div className="min-h-screen bg-gray-100 flex items-start justify-center px-4 py-12 md:py-20">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-sm border-t-[6px] border-primary-600 overflow-hidden">
          <div className="px-8 pt-8 pb-6 border-b border-gray-100 flex justify-center">
            <img src={BRAND_WORDMARK} alt={BRANDING.nomeBreve} className="h-14 w-auto" />
          </div>

          <div className="px-8 py-8 text-center">
            {stato.fase === 'caricamento' && (
              <Loader2 className="w-7 h-7 animate-spin text-primary-500 mx-auto" />
            )}

            {stato.fase === 'da_confermare' && (
              <>
                <h1 className="text-xl font-bold text-gray-900">{titoloPagina}</h1>
                <p className="mt-3 text-sm text-neutral-600 leading-relaxed">
                  {azione === 'preferenze'
                    ? 'Al momento ricevi le nostre comunicazioni commerciali a questo indirizzo:'
                    : 'Vuoi smettere di ricevere le nostre comunicazioni commerciali a questo indirizzo?'}
                </p>
                <p className="mt-2 text-sm font-medium text-gray-900 break-all">{stato.email}</p>
                <button
                  type="button"
                  onClick={disiscrivi}
                  disabled={lavorando}
                  className="mt-6 inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 hover:bg-primary-700 px-6 py-3 text-sm font-semibold text-white disabled:opacity-60"
                >
                  {lavorando ? <Loader2 className="w-4 h-4 animate-spin" /> : <MailX className="w-4 h-4" />}
                  {azione === 'preferenze' ? 'Non voglio più riceverle' : 'Sì, non voglio più riceverle'}
                </button>
                <p className="mt-4 text-xs text-neutral-400 leading-relaxed">
                  Continuerai a ricevere solo le comunicazioni legate a ordini e documenti.
                </p>
              </>
            )}

            {stato.fase === 'disiscritto' && (
              <Esito
                icona="ok"
                titolo="Fatto"
                testo={`Non riceverai più le nostre comunicazioni commerciali all'indirizzo ${stato.email}.`}
              />
            )}

            {stato.fase === 'gia_fuori' && (
              <Esito
                icona="ok"
                titolo="Indirizzo già disiscritto"
                testo={`L'indirizzo ${stato.email} non riceve più le nostre comunicazioni commerciali.`}
              />
            )}

            {stato.fase === 'iscrizione' && (
              <Esito
                icona={stato.esito === 'confermata' || stato.esito === 'gia_attiva' ? 'ok' : 'no'}
                titolo={TESTI_ISCRIZIONE[stato.esito].titolo}
                testo={TESTI_ISCRIZIONE[stato.esito].testo}
              />
            )}

            {stato.fase === 'non_valido' && (
              <Esito icona="no" titolo="Link non valido" testo="Questo link non risulta più valido." />
            )}

            {stato.fase === 'errore' && (
              <Esito
                icona="no"
                titolo="Qualcosa non ha funzionato"
                testo="Riprova fra qualche minuto. Se non va, rispondi a una delle nostre email e ti togliamo noi."
              />
            )}
          </div>

          <div className="px-8 py-5 bg-gray-50 border-t border-gray-100 text-center text-xs text-neutral-500 leading-relaxed">
            <strong className="text-primary-700">{BRANDING.ragioneSociale}</strong>
            <br />
            {[BRANDING.indirizzo, brandingLocalita()].filter(Boolean).join(' - ')}
            {BRANDING.partitaIva && (
              <>
                <br />
                P.IVA {BRANDING.partitaIva}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Esito({ icona, titolo, testo }: { icona: 'ok' | 'no'; titolo: string; testo: string }) {
  const Icona = icona === 'ok' ? CheckCircle2 : XCircle;
  return (
    <>
      <Icona className={`w-10 h-10 mx-auto ${icona === 'ok' ? 'text-green-600' : 'text-neutral-400'}`} />
      <h1 className="mt-3 text-xl font-bold text-gray-900">{titolo}</h1>
      <p className="mt-3 text-sm text-neutral-600 leading-relaxed break-words">{testo}</p>
    </>
  );
}
